'use client'

import { useEffect, useRef, useState, useCallback } from 'react'
import { X } from 'lucide-react'
import { BOOK_A_DEMO_URL } from '@/lib/constants'
import { AttributedCtaIframe } from '@/components/conversion/AttributedCtaIframe'

// ── Types ──────────────────────────────────────────────────

interface DemoBookingModalProps {
  isOpen: boolean
  onClose: () => void
  /** Which surface opened this modal, for CTA attribution. Defaults to 'modal'. */
  page?: string
}

// ── Component ──────────────────────────────────────────────

export function DemoBookingModal({ isOpen, onClose, page = 'modal' }: DemoBookingModalProps) {
  const [iframeLoaded, setIframeLoaded] = useState(false)
  const modalRef = useRef<HTMLDivElement>(null)
  const closeButtonRef = useRef<HTMLButtonElement>(null)
  const previousFocusRef = useRef<HTMLElement | null>(null)

  // ── Focus trap + body scroll lock ──────────────────────

  useEffect(() => {
    if (!isOpen) return

    // Store the previously focused element to restore later
    previousFocusRef.current = document.activeElement as HTMLElement

    // Prevent body scroll
    const originalOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'

    // Focus the close button when modal opens
    requestAnimationFrame(() => {
      closeButtonRef.current?.focus()
    })

    return () => {
      document.body.style.overflow = originalOverflow
      // Restore focus to the element that triggered the modal
      previousFocusRef.current?.focus()
    }
  }, [isOpen])

  // ── Escape key handler ─────────────────────────────────

  useEffect(() => {
    if (!isOpen) return

    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        onClose()
        return
      }

      // Focus trap: Tab cycling within modal
      if (e.key === 'Tab' && modalRef.current) {
        const focusable = modalRef.current.querySelectorAll<HTMLElement>(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        )

        if (focusable.length === 0) return

        const first = focusable[0]
        const last = focusable[focusable.length - 1]

        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault()
          last.focus()
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault()
          first.focus()
        }
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [isOpen, onClose])

  // Reset iframe loaded state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setIframeLoaded(false)
    }
  }, [isOpen])

  const handleBackdropClick = useCallback(
    (e: React.MouseEvent) => {
      if (e.target === e.currentTarget) {
        onClose()
      }
    },
    [onClose]
  )

  return (
    <div
      className={`anim-overlay fixed inset-0 z-50 flex items-center justify-center p-4 ${isOpen ? 'visible' : ''}`}
      onClick={handleBackdropClick}
      style={{
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        backdropFilter: 'blur(8px)',
        WebkitBackdropFilter: 'blur(8px)',
      }}
      role="presentation"
    >
      {isOpen && (
        <div
          ref={modalRef}
          className={`anim-scale-in glass-card relative w-full max-w-2xl overflow-hidden ${isOpen ? 'in-view' : ''}`}
          role="dialog"
          aria-modal="true"
          aria-label="Book a demo"
        >
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-[var(--border-default)]">
            <h2 className="text-lg font-semibold text-white">
              Book a Demo
            </h2>
            <button
              ref={closeButtonRef}
              onClick={onClose}
              className="flex items-center justify-center w-8 h-8 rounded-lg
                         text-[var(--text-muted)] hover:text-white
                         hover:bg-[var(--bg-subtle)]
                         transition-colors duration-[var(--transition-fast)]"
              aria-label="Close booking modal"
            >
              <X size={18} />
            </button>
          </div>

          {/* Body */}
          <div className="relative" style={{ minHeight: '600px' }}>
            {/* Loading Spinner */}
            {!iframeLoaded && (
              <div className="absolute inset-0 flex items-center justify-center bg-[var(--bg-surface)]">
                <div className="flex flex-col items-center gap-3">
                  <div
                    className="w-8 h-8 border-2 border-[var(--border-hover)] border-t-[var(--brand-red)] rounded-full animate-spin"
                    role="status"
                  >
                    <span className="sr-only">Loading booking calendar…</span>
                  </div>
                  <p className="text-sm text-[var(--text-muted)]">
                    Loading calendar…
                  </p>
                </div>
              </div>
            )}

            <AttributedCtaIframe
              src={BOOK_A_DEMO_URL}
              page={page}
              cta="Demo Booking Modal"
              title="Book a demo with BuilderLync"
              className="w-full border-0"
              style={{
                minHeight: '600px',
                opacity: iframeLoaded ? 1 : 0,
                transition: 'opacity 0.3s ease',
              }}
              onLoad={() => setIframeLoaded(true)}
              allow="camera; microphone"
            />
          </div>
        </div>
      )}
    </div>
  )
}
